import React from "react";
import { TimelineCursor } from "../../atoms";
import { CHART_COLORS } from "@/constants/chart-colors";
import type { TimelineSectionProps } from "./TimelineSection.types";

const TimelineSection: React.FC<TimelineSectionProps> = ({
  currentDate,
  timeProgress,
  visibleDateRange,
  data,
  cursorRef,
  timelineRef,
  eventMarkers,
  cashTransitionMarkers,
  zeroCashDayMarker,
  projectEndDayMarker,
  financialEventMarkers,
  timeSegmentationMarkers,
  revisionMarkers = [],
  showRevisionMarkers = true,
  onToggleRevisionMarkers,
  onMouseDown,
  onEventNavigation,
}) => {
  const today = new Date();
  const [openTooltipId, setOpenTooltipId] = React.useState<string | null>(null);

  // Helpers: normalize and validate dates to avoid "Invalid Date"
  const toDate = (d: any): Date | null => {
    if (d === null || d === undefined) return null;
    if (d instanceof Date) return isNaN(d.getTime()) ? null : d;
    // Firestore Timestamp has toDate()
    if (typeof d === "object") {
      if (typeof d.toDate === "function") {
        const dt = d.toDate();
        return dt instanceof Date && !isNaN(dt.getTime()) ? dt : null;
      }
      // Some serializations store seconds/nanoseconds
      if (typeof d.seconds === "number") {
        const ms = d.seconds * 1000 + (d.nanoseconds ? Math.floor(d.nanoseconds / 1e6) : 0);
        const dt = new Date(ms);
        return isNaN(dt.getTime()) ? null : dt;
      }
    }
    const parsed = new Date(d);
    return isNaN(parsed.getTime()) ? null : parsed;
  };

  const formatDateSafe = (d: Date | null | undefined) => (d ? d.toLocaleDateString() : "Unknown date");

  return (
    <>
      {/* Timeline scrubber */}
      <div className="border-b border-white/10 bg-surface-elevated/80 p-4">
        <div className="mb-2 flex justify-between text-sm text-white/65">
          <span>{visibleDateRange.startDate.toLocaleDateString()}</span>
          <div className="flex items-center gap-3">
            {revisionMarkers.length > 0 && onToggleRevisionMarkers ? (
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  onToggleRevisionMarkers();
                }}
                className={`rounded-full px-2 py-0.5 text-[11px] font-medium transition-colors ${
                  showRevisionMarkers
                    ? "bg-sky-500/20 text-sky-200"
                    : "bg-white/10 text-white/45"
                }`}
              >
                ◆ Revisions {showRevisionMarkers ? "on" : "off"}
              </button>
            ) : null}
            <span>{currentDate.toLocaleDateString()}</span>
          </div>
          <span>{visibleDateRange.endDate.toLocaleDateString()}</span>
        </div>

        <div
          ref={timelineRef}
          className="relative h-16 cursor-pointer rounded-[14px]"
          onMouseDown={onMouseDown}
        >
          {/* Background layer — overflow hidden for rounded corners only */}
          <div className="pointer-events-none absolute inset-0 overflow-hidden rounded-[14px] bg-surface-secondary ring-1 ring-white/10" />

          {/* Timeline markers */}
          <div className="absolute inset-0 flex items-center">
            {/* Pre-startup historical section */}
            <div
              className="absolute left-0 top-0 bottom-0 bg-white/15 opacity-50 rounded-l-lg border-r-2 border-white/20 border-dashed"
              style={{
                width: `${Math.min(
                  100,
                  Math.max(
                    0,
                    ((data.startDate.getTime() -
                      visibleDateRange.startDate.getTime()) /
                      (visibleDateRange.endDate.getTime() -
                        visibleDateRange.startDate.getTime())) *
                      100,
                  ),
                )}%`,
              }}
              title="Pre-startup period (historical context)"
            />

            {/* Past, present, future sections */}
            <div
              className="absolute bottom-0 left-0 top-0 rounded-l-[14px] bg-focus/20 opacity-40"
              style={{
                width: `${Math.min(
                  100,
                  Math.max(
                    0,
                    ((today.getTime() - visibleDateRange.startDate.getTime()) /
                      (visibleDateRange.endDate.getTime() -
                        visibleDateRange.startDate.getTime())) *
                      100,
                  ),
                )}%`,
              }}
            />

            {/* Time segmentation lines */}
            {timeSegmentationMarkers.map((marker, index) => (
              <div
                key={`time-segment-${marker.type}-${index}`}
                className={`pointer-events-none absolute top-0 bottom-0 w-0 ${
                  marker.type === "biweek"
                    ? "border-l border-dotted border-white/[0.06]"
                    : marker.type === "month"
                      ? "border-l border-dotted border-white/10"
                      : "border-l-2 border-solid border-white/20"
                }`}
                style={{
                  left: `${marker.progress * 100}%`,
                }}
              />
            ))}

            {/* Startup start date marker */}
            <div
              className="pointer-events-none absolute inset-y-0 z-10"
              style={{
                left: `${
                  ((data.startDate.getTime() -
                    visibleDateRange.startDate.getTime()) /
                    (visibleDateRange.endDate.getTime() -
                      visibleDateRange.startDate.getTime())) *
                  100
                }%`,
              }}
              title="Startup official start date"
            >
              <div className="absolute inset-y-0 left-1/2 w-0 -translate-x-1/2 border-l-4 border-solid border-focus" />
              <div className="absolute top-0 left-1/2 h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-page bg-focus shadow-[0_0_12px_rgba(59,130,246,0.45)]" />
              <div className="absolute bottom-full left-1/2 mb-2 -translate-x-1/2">
                <div className="whitespace-nowrap rounded bg-focus px-2 py-1 text-xs text-white shadow-[0_0_12px_rgba(59,130,246,0.35)]">
                  Start Date
                </div>
              </div>
            </div>

            {/* Project end date marker */}
            {projectEndDayMarker ? (
              <div
                className="pointer-events-none absolute inset-y-0 z-10"
                style={{ left: `${projectEndDayMarker.progress * 100}%` }}
                title="Project end date"
              >
                <div className="absolute inset-y-0 left-1/2 w-0 -translate-x-1/2 border-l-4 border-dashed border-purple-400" />
                <div className="absolute top-0 left-1/2 h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-page bg-purple-400 shadow-[0_0_12px_rgba(192,132,252,0.45)]" />
                <div className="absolute top-full left-1/2 mt-2 -translate-x-1/2">
                  <div className="whitespace-nowrap rounded border border-purple-400/50 bg-purple-400/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-purple-300">
                    Project End Date
                  </div>
                </div>
              </div>
            ) : null}

            {/* Current position scrubber */}
            <TimelineCursor timeProgress={timeProgress} cursorRef={cursorRef} />

            {/* Prominent zero-cash day marker */}
            {zeroCashDayMarker ? (
              <div
                className="pointer-events-none absolute inset-y-0 z-20"
                style={{ left: `${zeroCashDayMarker.progress * 100}%` }}
              >
                <div
                  className="absolute inset-y-0 left-1/2 w-0 -translate-x-1/2 border-l-[3px] border-dashed border-warn shadow-[0_0_12px_rgba(251,191,36,0.45)]"
                  title={zeroCashDayMarker.label}
                />
                <div className="absolute -top-1 left-1/2 -translate-x-1/2">
                  <div className="flex flex-col items-center">
                    <div className="h-3 w-3 rounded-full border-2 border-page bg-warn shadow-glow" />
                    <div className="mt-1 whitespace-nowrap rounded-md border border-warn/50 bg-warn/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-warn">
                      Zero cash
                    </div>
                    <div className="mt-0.5 whitespace-nowrap text-[10px] font-semibold text-warn/90">
                      {zeroCashDayMarker.label}
                    </div>
                  </div>
                </div>
              </div>
            ) : null}

            {/* Secondary cash transition markers (e.g. after funding recovery) */}
            {cashTransitionMarkers.map((transition) => (
              <div
                key={transition.id}
                className="absolute top-0 bottom-0 w-0.5 bg-opacity-80 pointer-events-none"
                style={{
                  left: `${transition.progress * 100}%`,
                  backgroundColor: transition.color,
                }}
                title={transition.description}
              >
                {/* Cash transition marker dot */}
                <div
                  className="absolute -top-1 left-1/2 transform -translate-x-1/2 w-2 h-2 rounded-full border border-white shadow-none"
                  style={{ backgroundColor: transition.color }}
                />
              </div>
            ))}

            {revisionMarkers.map((marker) => (
              <div
                key={marker.id}
                className="pointer-events-none absolute inset-y-0 z-[15]"
                style={{ left: `${marker.progress * 100}%` }}
                title={marker.label}
              >
                <div className="absolute inset-y-0 left-1/2 w-0 -translate-x-1/2 border-l border-dashed border-sky-400/70" />
                <div className="absolute bottom-1 left-1/2 -translate-x-1/2 text-sky-300">
                  ◆
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Consolidated Indicators (Revenue + Expense + Events + Revisions) */}
      {(financialEventMarkers.length > 0 ||
        eventMarkers.length > 0 ||
        revisionMarkers.length > 0) && (
        <div className="px-4 py-1" onClick={() => setOpenTooltipId(null)}>
          <div className="relative h-16"> {/* Increased height for larger vertical spacing */}
            <div className="relative h-16">
              {(() => {
                // Group all markers by date proximity
                const groupedByDate: Map<string, {
                  progress: number;
                  expenses: typeof financialEventMarkers;
                  revenues: typeof financialEventMarkers;
                  events: typeof eventMarkers;
                  date: Date | null;
                }> = new Map();

                const COLLISION_THRESHOLD = 0.02; // 2% of timeline for same "day"

                // Process financial event markers
                financialEventMarkers.forEach((marker) => {
                  let groupKey: string | null = null;
                  for (const [key, group] of groupedByDate.entries()) {
                    if (Math.abs(group.progress - marker.progress) < COLLISION_THRESHOLD) {
                      groupKey = key;
                      break;
                    }
                  }

                  if (!groupKey) {
                    groupKey = `${marker.progress.toFixed(4)}`;
                    groupedByDate.set(groupKey, {
                      progress: marker.progress,
                      expenses: [],
                      revenues: [],
                      events: [],
                      date: toDate(marker.date),
                    });
                  }

                  const group = groupedByDate.get(groupKey)!;
                  
                  if (marker.type.includes('expense') || marker.type === 'one-time-expense') {
                    group.expenses.push(marker);
                  } else if (marker.type.includes('revenue')) {
                    group.revenues.push(marker);
                  }
                });

                // Process regular event markers
                eventMarkers.forEach((marker) => {
                  let groupKey: string | null = null;
                  for (const [key, group] of groupedByDate.entries()) {
                    if (Math.abs(group.progress - marker.progress) < COLLISION_THRESHOLD) {
                      groupKey = key;
                      break;
                    }
                  }

                  if (!groupKey) {
                    groupKey = `${marker.progress.toFixed(4)}`;
                    groupedByDate.set(groupKey, {
                      progress: marker.progress,
                      expenses: [],
                      revenues: [],
                      events: [],
                      date: toDate(marker.date),
                    });
                  }

                  const group = groupedByDate.get(groupKey)!;
                  group.events.push(marker);
                });

                revisionMarkers.forEach((marker) => {
                  let groupKey: string | null = null;
                  for (const [key, group] of groupedByDate.entries()) {
                    if (Math.abs(group.progress - marker.progress) < COLLISION_THRESHOLD) {
                      groupKey = key;
                      break;
                    }
                  }

                  if (!groupKey) {
                    groupKey = `${marker.progress.toFixed(4)}-revision`;
                    groupedByDate.set(groupKey, {
                      progress: marker.progress,
                      expenses: [],
                      revenues: [],
                      events: [],
                      date: toDate(marker.date),
                    });
                  }

                  const group = groupedByDate.get(groupKey)!;
                  group.events.push({
                    id: marker.id,
                    date: marker.date,
                    type: "custom",
                    description: marker.label,
                    priority: "medium",
                    progress: marker.progress,
                    color: marker.color,
                    name: marker.name,
                  } as any);
                });

                // Create consolidated markers for each group (max 3: revenue, expense, event)
                const consolidatedMarkers: Array<{
                  id: string;
                  progress: number;
                  type: 'revenue' | 'expense' | 'event';
                  items: any[];
                  color: string;
                  date: Date;
                  position: number; // Dynamic position based on what's actually present
                }> = [];

                groupedByDate.forEach((group, key) => {
                  let currentPosition = 0; // Dynamic position counter for this group

                  // ensure consolidated.date is a valid Date (fallback to currentDate prop)
                  const consolidatedDate = group.date ?? currentDate;

                  // Add revenue marker if there are revenues
                  if (group.revenues.length > 0) {
                    consolidatedMarkers.push({
                      id: `revenue-${key}`,
                      progress: group.progress,
                      type: 'revenue',
                      items: group.revenues,
                      color: '#10b981', // Green for revenues
                      date: consolidatedDate,
                      position: currentPosition++,
                    });
                  }

                  // Add expense marker if there are expenses
                  if (group.expenses.length > 0) {
                    consolidatedMarkers.push({
                      id: `expense-${key}`,
                      progress: group.progress,
                      type: 'expense',
                      items: group.expenses,
                      color: CHART_COLORS.expense,
                      date: consolidatedDate,
                      position: currentPosition++,
                    });
                  }

                  // Add event marker if there are events
                  if (group.events.length > 0) {
                    consolidatedMarkers.push({
                      id: `event-${key}`,
                      progress: group.progress,
                      type: 'event',
                      items: group.events,
                      color: group.events[0].color, // Use first event's color
                      date: consolidatedDate,
                      position: currentPosition++,
                    });
                  }
                });

                return consolidatedMarkers.map((consolidated) => {
                  const isTooltipOpen = openTooltipId === consolidated.id;

                  return (
                    <div
                      key={consolidated.id}
                      className="absolute cursor-pointer"
                      style={{ 
                        left: `${consolidated.progress * 100}%`,
                        top: '-4px'
                      }}
                    >
                      {/* Marker symbol positioned at exact timeline location */}
                      <div
                        className="absolute left-1/2 transform -translate-x-1/2 flex items-center justify-center w-4 h-4 text-xs font-bold hover:scale-125 transition-transform duration-200 z-10 group"
                        style={{ 
                          color: consolidated.color,
                          top: `${consolidated.position * 20 + 4}px` // Stack vertically: revenue=4px, expense=24px, event=44px
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (consolidated.items.length === 1) {
                            // If only one item, navigate directly
                            onEventNavigation(consolidated.date);
                          } else {
                            // If multiple items, toggle tooltip
                            setOpenTooltipId(isTooltipOpen ? null : consolidated.id);
                          }
                        }}
                        title={
                          consolidated.items.length === 1
                            ? `Click to navigate to: ${consolidated.items[0].name || consolidated.items[0].description}`
                            : `Click to view ${consolidated.items.length} ${consolidated.type}(s) on ${formatDateSafe(consolidated.date)}`
                        }
                      >
                        {consolidated.type === 'revenue' ? '▲' : 
                         consolidated.type === 'expense' ? '▼' : '●'}
                        
                        {/* Multi-item indicator */}
                        {consolidated.items.length > 1 && (
                          <div className="absolute -top-1 -right-1 w-3 h-3 bg-brand text-white rounded-full flex items-center justify-center"
                               style={{ fontSize: '10px', fontWeight: 'bold' }}>
                            {consolidated.items.length}
                          </div>
                        )}

                        {/* Single-item hover tooltip */}
                        {consolidated.items.length === 1 && (
                          <div className="absolute left-1/2 transform -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-20"
                               style={{ top: '28px' }}> {/* Positioned below the stacked markers */}
                            <div className="bg-surface-elevated text-white text-xs px-4 py-3 rounded-lg shadow-glow min-w-[200px] max-w-sm">
                              <div
                                className="font-semibold mb-2"
                                style={{ color: consolidated.color }}
                              >
                                {consolidated.type.toUpperCase()} - {formatDateSafe(consolidated.date)}
                              </div>
                              <div className="text-left">
                                <div className="font-medium">{consolidated.items[0].name || consolidated.items[0].description}</div>
                                {consolidated.items[0].category && <div className="text-white/40 text-xs">{consolidated.items[0].category}</div>}
                                {consolidated.items[0].amount && (
                                  <div className="font-bold">
                                    ${consolidated.items[0].amount.toLocaleString()}{consolidated.items[0].isOneTime ? '' : '/mo'}
                                  </div>
                                )}
                              </div>
                              <div className="text-white/40 text-xs mt-2">Click to navigate</div>
                              {/* Tooltip arrow */}
                              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-b-4 border-transparent border-b-gray-900" />
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Click-based tooltip for multiple items */}
                      {isTooltipOpen && consolidated.items.length > 1 && (
                        <div className="absolute pointer-events-auto z-30"
                             style={{
                               top: '28px', // Positioned below the stacked markers
                               left: '50%',
                               transform: 'translateX(-50%)'
                             }}>
                          <div className="bg-surface-elevated text-white text-xs px-4 py-3 rounded-lg shadow-glow min-w-[280px] max-w-sm">
                            <div className="flex justify-between items-center mb-2">
                              <div
                                className="font-semibold"
                                style={{ color: consolidated.color }}
                              >
                                {consolidated.type.toUpperCase()}S - {formatDateSafe(consolidated.date)}
                              </div>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setOpenTooltipId(null);
                                }}
                                className="text-white/40 hover:text-white ml-2 text-sm leading-none"
                              >
                                ×
                              </button>
                            </div>
                            <div className="space-y-1 max-h-32 overflow-y-auto">
                              {consolidated.items.map((item: any, itemIndex: number) => (
                                <div 
                                  key={itemIndex} 
                                  className="text-left cursor-pointer hover:bg-white/10 p-1 rounded"
                                  onClick={() => {
                                    const itemDate = toDate(item.date) ?? consolidated.date ?? currentDate;
                                    onEventNavigation(itemDate);
                                    setOpenTooltipId(null);
                                  }}
                                >
                                  <div className="font-medium">{item.name || item.description}</div>
                                  {item.category && <div className="text-white/40 text-xs">{item.category}</div>}
                                  {item.amount && (
                                    <div className="font-bold">
                                      ${item.amount.toLocaleString()}{item.isOneTime ? '' : '/mo'}
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                            {/* Tooltip arrow */}
                            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-b-4 border-transparent border-b-gray-900" />
                          </div>
                        </div>
                      )}
                    </div>
                  );
                });
              })()}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default TimelineSection;